import { type Getter, type Setter, atom } from "jotai";
import { atomFamily } from "jotai/utils";

import {
	HttpRequestPageSchema,
	type HttpRequestPage as HttpRequestPageType,
} from "@moment/api-collab";
import { Logger } from "@moment/logging";

import {
	HttpRequestCollabSubscriptionError,
	HttpRequestMisconfiguredError,
	HttpRequestNotLoadedError,
	HttpRequestPersistError,
	HttpRequestUpdateHaltedError,
	HttpRequestValidationError,
} from "./errors";
import type {
	HttpRequestPageContainer,
	HttpRequestPageParams,
	HttpRequestPageRecord,
	RequestPageCollabConnection,
} from "./request-page.types";

const logger = new Logger("request-page");

// =============================================================================
// Internal State Atom
// =============================================================================

/**
 * @internal INTERNAL atom holding the HttpRequestPageRecord value and params. Use $httpRequestPage
 * for reading/writing.
 */
const $httpRequestPageValue = atomFamily((key: string) => {
	// IMPORTANT: MUST CAST here to work around a typing bug.
	//
	// With `strictNullChecks: false`, `null` is assignable to function types, so `atom(null)`
	// matches jotai's `atom(read: Function): Atom<T>` (read-only) overload. Casting `null` to
	// `HttpRequestPageContainer | null` seems to resolve the issue.
	//
	// NOTE: `atom<HttpRequestPageContainer | null>` DOES NOT fix the situation. You MUST cast.
	const valueAtom = atom(null as HttpRequestPageContainer | null);
	valueAtom.debugLabel = `httpRequestPageValue::${key}`;
	return valueAtom;
});

// =============================================================================
// Public Read/Write Atom
// =============================================================================

export type HttpRequestPageAction =
	| {
			type: "set";
			updater: HttpRequestPageType | ((previous: HttpRequestPageType) => HttpRequestPageType);
			bumpImportEpoch?: boolean;
	  }
	| { type: "reset" };

/**
 * Provides read/write access to an HttpRequestPage instance with optional collab and persistence.
 */
export const $httpRequestPage = atomFamily((key: string) => {
	const $stateAtom = atom(
		(get): HttpRequestPageRecord | null => get($httpRequestPageValue(key))?.record ?? null,

		async (get, set, action: HttpRequestPageAction) => {
			//////////////////////////////////////////////////////////////////////////////////////
			// IMPORTANT: SYNCHRONOUS ZONE. DO NOT PUT ANYTHING SLOW HERE. ///////////////////////
			const container = get($httpRequestPageValue(key));

			// NOTE: We choose NOT to error out here if container is not initialized. `reset` as an
			// action is supposed to be safe and idempotent. We might change our minds later.
			if (action.type === "reset") {
				if (container) {
					set($httpRequestPageValue(key), { ...container, record: null });
				}
				return;
			}

			if (!container || !container.record) {
				logger.error("Cannot update http request: not loaded", { key });
				const error = new HttpRequestNotLoadedError();
				set($httpRequestPageValue(key), { record: null, params: { key }, error });
				throw error;
			}

			const { record: currentRecord, params, error, abortController } = container;

			// Check if updates are halted.
			if (error) {
				throw new HttpRequestUpdateHaltedError(error);
			}

			const nextRequest =
				typeof action.updater === "function"
					? action.updater(currentRecord.request)
					: action.updater;

			const parsed = HttpRequestPageSchema.safeParse(nextRequest);
			if (!parsed.success) {
				throw new HttpRequestValidationError(parsed.error);
			}
			const validated = parsed.data;

			const nextRecord: HttpRequestPageRecord = {
				source:
					currentRecord.source === "remote"
						? "memory"
						: (currentRecord.source ?? "memory"),
				request: validated,
				importEpoch: currentRecord.importEpoch + (action.bumpImportEpoch ? 1 : 0),
			};

			// Update state. NEVER change params, error, or abortController.
			set($httpRequestPageValue(key), {
				...container,
				record: nextRecord,
				params,
				error,
				abortController,
			});
			// IMPORTANT: END SYNCHRONOUS ZONE. //////////////////////////////////////////////////
			//////////////////////////////////////////////////////////////////////////////////////

			// ========== ASYNC ZONE (CALLBACKS) ==========
			// Send to collab if enabled
			if (params.sendToCollab) {
				await params.sendToCollab(validated).catch((err) => {
					logger.error("Failed to send to collab", { key, err });
				});
			}

			// Persist to disk if enabled
			if (params.onPersist) {
				await params.onPersist(validated).catch((err) => {
					logger.error("Failed to persist http request", { key, err });

					// Set critical error
					const currentContainer = get($httpRequestPageValue(key));
					if (!currentContainer) {
						return;
					}

					const persistError = new HttpRequestPersistError(err);
					set($httpRequestPageValue(key), { ...currentContainer, error: persistError });
				});
			}
		}
	);

	$stateAtom.debugLabel = `httpRequestPage::${key}`;
	return $stateAtom;
});

export type ApplyExternalHttpRequestPageInput = {
	key: string;
	request: HttpRequestPageType;
};

/**
 * Applies external request page changes from the file watcher (e.g. agent YAML edits).
 *
 * Skips onPersist because the change already came from the filesystem — writing it back
 * would be redundant.
 *
 * Unlike metadata (which has $syncNewPagesToCollab to handle collab sync separately),
 * request pages have no other mechanism to propagate content changes to remote
 * collaborators. So we DO call sendToCollab here. The isEqual guard in the file watcher
 * prevents double-syncing when a remote change round-trips through onPersist → disk →
 * file watcher: by that point the local state already matches, so this atom is never
 * reached.
 */
export const $applyExternalHttpRequestPage = atom(
	null,
	(get, set, input: ApplyExternalHttpRequestPageInput) => {
		const { key, request } = input;
		const container = get($httpRequestPageValue(key));
		if (!container || !container.record) {
			logger.warn("Cannot apply external http request: state not initialized", { key });
			return;
		}

		const { record: currentRecord, params, error, abortController, connection } = container;
		const nextRecord: HttpRequestPageRecord = {
			source: "disk",
			request,
			importEpoch: currentRecord.importEpoch + 1,
		};

		set($httpRequestPageValue(key), {
			record: nextRecord,
			params,
			error,
			abortController,
			connection,
		});

		if (params.sendToCollab) {
			void params.sendToCollab(request).catch((err) => {
				logger.error("Failed to send external http request to collab", { key, err });
			});
		}
	}
);
$applyExternalHttpRequestPage.debugLabel = "applyExternalHttpRequestPage";

// =============================================================================
// Collab Listeners
// =============================================================================

/**
 * Starts the collab listeners (remote changes) for a given http request page key.
 * Called from $loadHttpRequestPage after the container is set.
 */
function startCollabListeners(
	key: string,
	abortController: AbortController,
	get: Getter,
	set: Setter
): void {
	const container = get($httpRequestPageValue(key));
	if (!container) {
		logger.error("Cannot start collab listeners: container not initialized", { key });
		return;
	}
	const { remoteChanges } = container.params;

	if (!remoteChanges) {
		return;
	}

	//
	// SUBSCRIBE TO REMOTE CHANGES TO HTTP REQUEST.
	//

	void (async function listenForRemoteChanges() {
		try {
			logger.info("Starting remote changes listener", { key });

			const iterator = remoteChanges({ signal: abortController.signal });
			for await (const request of iterator) {
				if (abortController.signal.aborted) {
					break;
				}

				logger.debug("Received remote http request", { key });

				// Get current container
				const currentContainer = get($httpRequestPageValue(key));
				if (!currentContainer || !currentContainer.record) {
					logger.warn("Cannot apply remote http request: state not initialized", { key });
					continue;
				}
				const {
					record: currentRecord,
					params,
					error,
					abortController: ac,
					connection,
				} = currentContainer;

				// Create updated record with bumped importEpoch for remote changes
				const nextRecord: HttpRequestPageRecord = {
					source: "remote",
					request,
					importEpoch: currentRecord.importEpoch + 1,
				};

				// Update state while preserving params, error, and abortController.
				set($httpRequestPageValue(key), {
					record: nextRecord,
					params,
					error,
					abortController: ac,
					connection,
				});

				// Also persist remote changes
				if (params.onPersist) {
					await params.onPersist(request).catch((err) => {
						logger.error("Failed to persist remote changes", { key, err });

						// Set critical error
						const errorContainer = get($httpRequestPageValue(key));
						if (!errorContainer) {
							return;
						}

						const persistError = new HttpRequestPersistError(err);
						set($httpRequestPageValue(key), {
							...errorContainer,
							error: persistError,
						});
					});
				}
			}
		} catch (err) {
			if (abortController.signal.aborted) {
				return;
			}

			logger.error("Remote changes listener error", { key, err });

			// Set critical error.
			const errorContainer = get($httpRequestPageValue(key));
			if (!errorContainer) {
				return;
			}
			const collabError = new HttpRequestCollabSubscriptionError(err);
			set($httpRequestPageValue(key), {
				...errorContainer,
				error: collabError,
			});
		}
	})();
}

// =============================================================================
// Load/Unload Atoms
// =============================================================================

export type LoadHttpRequestPageInput = HttpRequestPageParams & {
	record: HttpRequestPageRecord;
	/** If true, load in read-only mode (no persistence required) */
	ssr?: boolean;
	/** Optional collab connection, only set for collab-enabled states */
	connection?: RequestPageCollabConnection;
};

/**
 * Loads an http request page. Once called and succeeds, the http request at `key` will be
 * fully initialized and (if configured to be writable) ready to accept updates.
 */
export const $loadHttpRequestPage = atom(null, (get, set, input: LoadHttpRequestPageInput) => {
	const { key, record, ssr = false, remoteChanges, sendToCollab, onPersist, connection } = input;

	if (!ssr && !sendToCollab && !onPersist) {
		throw new HttpRequestMisconfiguredError(
			"Failed to load metadata: persistence not configured"
		);
	}

	// Create AbortController for managing collab listeners lifecycle
	const abortController = new AbortController();

	const params: HttpRequestPageParams = { key, remoteChanges, sendToCollab, onPersist };
	set($httpRequestPageValue(key), { record, params, error: null, connection, abortController });

	// Start collab listeners after setting the container (ensures no race condition)
	if (!ssr) {
		startCollabListeners(key, abortController, get, set);
	}

	logger.info("Loaded http request page", { key, source: record.source, ssr });
});
$loadHttpRequestPage.debugLabel = "loadHttpRequestPage";

export type SetHttpRequestPageErrorInput = {
	key: string;
	error: HttpRequestPageContainer["error"];
};

/**
 * Sets an error on the http request page at `key`. Used by loaders when loading fails.
 */
export const $setHttpRequestPageError = atom(
	null,
	(get, set, input: SetHttpRequestPageErrorInput) => {
		const { key, error } = input;
		const container = get($httpRequestPageValue(key));
		const record = container?.record ?? null;
		set($httpRequestPageValue(key), { ...(container ?? {}), record, params: { key }, error });
	}
);
$setHttpRequestPageError.debugLabel = "setHttpRequestPageError";

/**
 * Unloads an http request page from `key`, freeing all resources associated with it.
 */
export const $unloadHttpRequestPage = atom(null, (get, set, key: string) => {
	// Abort collab listeners before cleaning up
	const container = get($httpRequestPageValue(key));
	if (container?.abortController) {
		logger.info("Stopping collab listeners", { key });
		container.abortController.abort();
	}

	set($httpRequestPageValue(key), null);
	$httpRequestPageValue.remove(key);
	$httpRequestPage.remove(key);
	logger.info("Unloaded http request page", { key });
});
$unloadHttpRequestPage.debugLabel = "unloadHttpRequestPage";

// =============================================================================
// Error Atom (Read-Only)
// =============================================================================

/**
 * Provides read-only access to the http request page error (for UI consumption).
 */
export const $httpRequestPageError = atomFamily((key: string) => {
	const errorAtom = atom((get) => get($httpRequestPageValue(key))?.error ?? null);
	errorAtom.debugLabel = `httpRequestPageError::${key}`;
	return errorAtom;
});

// =============================================================================
// Connection Accessor (Read-Only)
// =============================================================================

/**
 * Provides read-only access to the collab connection (for hooks).
 * Only available for collab-enabled request page states.
 */
export const $httpRequestPageConnection = atomFamily((key: string) => {
	const connectionAtom = atom((get) => get($httpRequestPageValue(key))?.connection ?? null);
	connectionAtom.debugLabel = `httpRequestPageConnection::${key}`;
	return connectionAtom;
});
